import { getTransformObject } from '~/common/photos/transforms'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { childAgeCSVStringToNumberArray } from '~/utility/child-ages/child-ages'
import { BookingThanks, bookingThanksSchema, pitchTypeSchema } from './schemas'

// Category type for use in multiple places
type CategoryType = {
  name: string
  slug: string
  [key: string]: unknown // For any other properties we might not know about
}

type ThanksAjaxSchemaPitchType = {
  campsite: {
    accessCode?: string | null
    cancellationPolicyNeedsTranslation?: boolean
    termsAndConditionsNeedsTranslation?: boolean
    point: string
    usePermittedArrivalDepartTimes: boolean
    arrivalTime?: string | null
    covidWarnings?: string[]
    notices?: string
  }
  allowedSubcategories: Array<{
    allowed: boolean
    name: string
  }>
  bringBedLinenHtml?: string | null
  negativeCategories: Array<CategoryType>
  primaryPhoto?: {
    caption?: string
    url: {
      masterImage: string
    }
  }
  restrictions?: Array<{ name: string; amenityIcon: string | null }>
  dimensionsDetailed?: {
    areaFt?: number | null
    areaM?: number | null
    customDimensions?: string | null
    depthFt?: number | null
    depthM?: number | null
    dimensionsType?: string | null
    dimensionsTypeCode?: string | null
    widthFt?: number | null
    widthM?: number | null
  }
}

type ThanksAjaxSchema = {
  accommodationCost?: {
    amount: number
    amountMinor: number
    currency: string
  }
  arrive?: string
  pitchType?: ThanksAjaxSchemaPitchType
  carRegistrationNumber?: string
  childAges?: string
  commission?: string | number
  depart?: string
  doorCode?: string | null
  estimatedTimeOfArrival?: string
  namesOfAllPartyMembers?: string
  newsletterSubscribed?: boolean
  typeAndApproxSizeOfUnit?: string
  unitType?: {
    name: string
  }
  dimensionsDepth?: string
  dimensionsWidth?: string
  dogs?: number
  pitchtypeNameWithUnitNumber?: string
  [key: string]: unknown // Using unknown is safer than any
}

export class BookingThanksRepositoryFetch {
  async getOneByToken(
    langCode: string,
    token: string,
    params?: Record<string, string>,
  ): Promise<BookingThanks> {
    const results = await $fetch<ThanksAjaxSchema>(
      getLangFallbackUrl(langCode, `/_/booking/thanks/${token}/`),
      {
        query: params,
      },
    )
    if (results) {
      return this.mapResult(results)
    }
    throw new Error('not-found')
  }

  protected mapResult(obj: ThanksAjaxSchema): BookingThanks {
    const pitchType = obj.pitchType
      ? this.mapPitchType(obj.pitchType)
      : undefined
    return bookingThanksSchema.parse({
      ...obj,
      arrive: obj.arrive,
      arrivalTime: obj.pitchType?.campsite?.arrivalTime,
      campsiteUsePermittedArrivalDepartTimes:
        pitchType?.campsite.usePermittedArrivalDepartTimes ?? false,
      campsiteCancellationPolicyNeedsTranslation:
        !!pitchType?.campsite.cancellationPolicyNeedsTranslation,
      campsiteTermsAndConditionsNeedsTranslation:
        !!pitchType?.campsite.termsAndConditionsNeedsTranslation,
      carRegistrationNumber: obj.carRegistrationNumber || '',

      childAges: childAgeCSVStringToNumberArray(obj.childAges),
      commission: Number(obj.commission || 0),
      depart: obj.depart,
      estimatedTimeOfArrival: obj.estimatedTimeOfArrival || '',
      namesOfAllPartyMembers: obj.namesOfAllPartyMembers || '',
      pitchType,
      typeAndApproxSizeOfUnit: obj.typeAndApproxSizeOfUnit || '',
      unitType: obj.unitType,
      dimensionsDepth: obj.dimensionsDepth,
      dimensionsWidth: obj.dimensionsWidth,
    })
  }

  private mapPitchType(pitchType: ThanksAjaxSchemaPitchType) {
    const campsite = {
      ...pitchType.campsite,
      cancellationPolicyNeedsTranslation:
        !!pitchType.campsite.cancellationPolicyNeedsTranslation,
      point: pitchType.campsite.point,
      termsAndConditionsNeedsTranslation:
        !!pitchType.campsite.termsAndConditionsNeedsTranslation,
      warnings: pitchType.campsite.covidWarnings || [],
    }
    return pitchTypeSchema.parse({
      ...pitchType,
      campsite,
      allowedSubcategories: pitchType.allowedSubcategories
        .filter((category) => category.allowed)
        .map((category) => category.name),
      negativeCategories: pitchType.negativeCategories.map((category) => {
        return { ...category, isPositive: false }
      }),
      primaryPhoto: pitchType.primaryPhoto
        ? {
            caption: pitchType.primaryPhoto.caption || '',
            url: getTransformObject(
              'bookingCampsite',
              pitchType.primaryPhoto.url.masterImage,
            ),
          }
        : null,
    })
  }
}
